import React, { useState, useEffect } from 'react';
import { ArrowLeft, Book, Calendar, Clock, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { apiService } from '../services/api';
import LoadingScreen from './LoadingScreen';
import StoryParametersBanner from './StoryParametersBanner';
import LullabyCarousel from './LullabyCarousel';
import MusicCarousel from './MusicCarousel';

const MyStoriesPage = ({ setActiveTab, isActive }) => {
  const [stories, setStories] = useState([]);
  const [selectedStory, setSelectedStory] = useState(null);
  const [currentStoryPage, setCurrentStoryPage] = useState(0);
  const [activeSection, setActiveSection] = useState('whizz'); // 'whizz' or 'z'
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingPage, setLoadingPage] = useState(false);

  const navigate = useNavigate();
  const { userData, currentUser } = useAuth();
  const { language, tr } = useLanguage();

  // Get stories from localStorage for current page (cache intelligent)
  const getUserStories = () => {
    try {
      const savedStories = localStorage.getItem(`userStories_page_${currentPage}_${activeSection}`);
      return savedStories ? JSON.parse(savedStories) : [];
    } catch (error) {
      console.warn('Error loading stories from localStorage:', error);
      return [];
    }
  };

  const userStoriesCache = getUserStories();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Always load from backend to get fresh data (professional approach)
    loadMyStories();
  }, [currentPage, activeSection]); // Reload when page or section changes

  useEffect(() => {
    // Refetch whenever the tab becomes visible so new stories appear without a page refresh
    if (isActive) {
      loadMyStories(true); // silent refresh: no spinner flash over existing stories
      // Second silent pass shortly after, in case a just-created story was still committing
      const retry = setTimeout(() => loadMyStories(true), 2500);
      return () => clearTimeout(retry);
    }
  }, [isActive]);

  const loadMyStories = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setLoadingPage(true);
      
      // First try to get stories from backend - MODIFIED: 30 stories per page
      try {
        const response = await apiService.user.getStories(currentPage, 30); // Get 30 stories per page
        if (response.data.success && response.data.stories) {
          // Trier les histoires par date de création (plus récent en premier)
          const sortedStories = response.data.stories.sort((a, b) => 
            new Date(b.createdAt || b.created_at) - new Date(a.createdAt || a.created_at)
          );
          setStories(sortedStories);
          
          // Update pagination info from backend response
          setTotalPages(response.data.totalPages || 1);
          setHasMore(response.data.hasMore || false);
          
          // Save to localStorage for current page only (cache intelligent)
          localStorage.setItem(`userStories_page_${currentPage}_${activeSection}`, JSON.stringify(sortedStories));
          console.log('✅ Stories loaded from backend:', sortedStories.length, `Page ${currentPage}/${response.data.totalPages || 1}`);
          
          return; // Successfully loaded from backend, exit early
        }
      } catch (backendError) {
        console.warn('⚠️ Backend stories not available, falling back to localStorage:', backendError.message);
      }
      
      // Fallback: Load from localStorage if backend fails (current page cache)
      const savedStories = localStorage.getItem(`userStories_page_${currentPage}_${activeSection}`);
      if (savedStories) {
        const localStories = JSON.parse(savedStories);
        // Trier aussi les histoires locales par date de création
        const sortedLocalStories = localStories.sort((a, b) => 
          new Date(b.createdAt || b.created_at) - new Date(a.createdAt || a.created_at)
        );
        setStories(sortedLocalStories);
        console.log('📱 Stories loaded from localStorage:', sortedLocalStories.length, `Page ${currentPage}`);
      }
      
    } catch (error) {
      console.error('❌ Error loading stories:', error);
    } finally {
      setLoading(false);
      setLoadingPage(false);
    }
  };

  // Filter stories based on active section
  const getFilteredStories = () => {
    if (activeSection === 'whizz') {
      // WhizZ stories (bedtime stories)
      return stories.filter(story => 
        story.type === 'bedtime' || 
        story.source === 'whizz' || 
        !story.type // Default to whizz for legacy stories
      );
    } else {
      // Z stories (music, games, etc.)
      return stories.filter(story => 
        story.type === 'music' || 
        story.type === 'game' || 
        story.type === 'z-story' ||
        story.source === 'z'
      );
    }
  };

  const filteredStories = getFilteredStories();

  // Get content counts for each section (for current page only)
  const whizzCount = stories.filter(story => 
    story.type === 'bedtime' || 
    story.source === 'whizz' || 
    !story.type
  ).length;

  const zCount = stories.filter(story => 
    story.type === 'music' || 
    story.type === 'game' || 
    story.type === 'z-story' ||
    story.source === 'z'
  ).length;

  // Pagination handlers
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToPage = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language, { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Format text for authentic book display based on age group - Editorial Standards 2025  
  const formatStoryText = (text, age) => {
    if (!text || text === "This part of the story is coming soon...") {
      return null;
    }
    
    // Clean text and split into sentences
    const sentences = text.split(/(?<=[.!?])\s+/).filter(sentence => sentence.trim());
    
    // For 3-4 ans: Large text, simple sentences, centered like picture books
    if (age && (age.includes('3-4') || age.includes('3') || age.includes('4'))) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          padding: '20px',
          paddingBottom: '50px'
        }}>
          
          {/* Zone de texte avec hauteur calculée */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: sentences.length <= 3 ? 'center' : 'space-between',
            flex: 1,
            minHeight: 0
          }}>
            {sentences.map((sentence, index) => (
              <p key={index} style={{
                textAlign: 'center',
                fontFamily: "'Comic Sans MS', 'Arial', sans-serif",
                fontSize: '20px',
                fontWeight: 'bold',
                lineHeight: '1.5',
                color: '#1f2937',
                margin: sentences.length <= 3 ? '10px 0' : '5px 0'
              }}>
                {sentence.trim()}
              </p>
            ))}
          </div>
          
          {/* Numéro de page dans le flow normal */}
          <div style={{
            textAlign: 'right',
            paddingTop: '10px',
            fontSize: '16px',
            fontWeight: 'bold',
            color: '#6366f1',
            fontFamily: "'Comic Sans MS', sans-serif"
          }}>
            Page {currentStoryPage + 1}
          </div>
        </div>
      );
    }
    
    // For 5-7 ans: Early readers format with intelligent distribution
    if (age && (age.includes('5-7') || age.includes('5') || age.includes('6') || age.includes('7'))) {
      // Limit to max 5 sentences per page for early readers
      const pageContent = sentences.slice(0, 5);
      
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          padding: '20px',
          paddingBottom: '50px'
        }}>
          
          {/* Zone de texte avec hauteur calculée */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: pageContent.length <= 4 ? 'center' : 'space-between',
            flex: 1,
            minHeight: 0
          }}>
            {pageContent.map((sentence, index) => (
              <p key={index} style={{
                textAlign: 'left',
                fontFamily: "'Arial', 'Comic Sans MS', sans-serif",
                fontSize: '18px',
                fontWeight: '600',
                lineHeight: '1.4',
                color: '#1f2937',
                margin: pageContent.length <= 4 ? '8px 0' : '4px 0'
              }}>
                {sentence.trim()}
              </p>
            ))}
          </div>
          
          {/* Numéro de page dans le flow normal */}
          <div style={{
            textAlign: 'right',
            paddingTop: '10px',
            fontSize: '16px',
            fontWeight: 'bold',
            color: '#6366f1',
            fontFamily: "'Arial', sans-serif"
          }}>
            Page {currentStoryPage + 1}
          </div>
        </div>
      );
    }
    
    // For 8-10 ans: Natural book-like text display
    if (age && (age.includes('8-10') || age.includes('8') || age.includes('9') || age.includes('10'))) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          padding: '20px',
          paddingBottom: '50px'
        }}>
          
          {/* Zone de texte avec hauteur calculée */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            flex: 1,
            minHeight: 0
          }}>
            <div style={{
              textAlign: 'left',
              fontFamily: "'Georgia', 'Times New Roman', serif",
              fontSize: '16px',
              fontWeight: '400',
              lineHeight: '1.8',
              color: '#1f2937',
              hyphens: 'auto',
              textIndent: '1.5em',
              letterSpacing: '0.3px'
            }}>
              {text.trim()}
            </div>
          </div>
          
          {/* Numéro de page dans le flow normal */}
          <div style={{
            textAlign: 'right',
            paddingTop: '10px',
            fontSize: '16px',
            fontWeight: 'bold',
            color: '#6366f1',
            fontFamily: "'Georgia', serif"
          }}>
            Page {currentStoryPage + 1}
          </div>
        </div>
      );
    }
    
    // For 11-13 ans: Natural book-like text display  
    if (age && (age.includes('11-13') || age.includes('11') || age.includes('12') || age.includes('13'))) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          padding: '20px',
          paddingBottom: '50px'
        }}>
          
          {/* Zone de texte avec hauteur calculée */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            flex: 1,
            minHeight: 0
          }}>
            <div style={{
              textAlign: 'left',
              fontFamily: "'Georgia', 'Times New Roman', serif",
              fontSize: '16px',
              fontWeight: '400',
              lineHeight: '1.8',
              color: '#1f2937',
              hyphens: 'auto',
              textIndent: '1.5em',
              letterSpacing: '0.3px'
            }}>
              {text.trim()}
            </div>
          </div>
          
          {/* Numéro de page dans le flow normal */}
          <div style={{
            textAlign: 'right',
            paddingTop: '10px',
            fontSize: '16px',
            fontWeight: 'bold',
            color: '#6366f1',
            fontFamily: "'Georgia', serif"
          }}>
            Page {currentStoryPage + 1}
          </div>
        </div>
      );
    }
    
    // Default: Simple paragraph formatting
    return (
      <p className="text-gray-800 text-justify font-serif" style={{ lineHeight: '1.5' }}>
        {text.trim()}
      </p>
    );
  };

  const getChapterInfo = (pageIndex, totalPages, age) => {
    // Standards éditoriaux 2025 - Chapitres par tranche d'âge
    if (typeof age === 'string') {
      
      // 3-4 ans (Albums) : Scènes simples ou pas de titre
      if (age.includes('3-4') || age.includes('3') || age.includes('4')) {
        return tr(`Scene ${pageIndex + 1} of ${totalPages}`, `Scène ${pageIndex + 1} sur ${totalPages}`);
      } 
      
      // 5-7 ans (Premières lectures) : Numéros simples
      else if (age.includes('5-7') || age.includes('5') || age.includes('6') || age.includes('7')) {
        return `${pageIndex + 1} / ${totalPages}`;
      } 
      
      // 8-10 ans (Romans juniors) : Premiers vrais chapitres (3-4 pages/chapitre)
      else if (age.includes('8-10') || age.includes('8') || age.includes('9') || age.includes('10')) {
        const chapter = Math.ceil((pageIndex + 1) / 4); // 4 pages par chapitre
        const totalChapters = Math.ceil(totalPages / 4);
        return tr(`Chapter ${chapter} of ${totalChapters} - Page ${pageIndex + 1}`, `Chapitre ${chapter} sur ${totalChapters} - Page ${pageIndex + 1}`);
      } 
      
      // 11-13 ans (Romans ados) : Chapitres longs avec parties si >50 pages
      else if (age.includes('11-13') || age.includes('11') || age.includes('12') || age.includes('13')) {
        if (totalPages > 50) {
          // Format "Partie + Chapitre" pour les longs romans
          const pagesPerChapter = 6; // 6 pages par chapitre
          const chaptersPerPart = 5; // 5 chapitres par partie
          
          const chapter = Math.ceil((pageIndex + 1) / pagesPerChapter);
          const part = Math.ceil(chapter / chaptersPerPart);
          const chapterInPart = ((chapter - 1) % chaptersPerPart) + 1;
          
          return tr(`Part ${part} - Chapter ${chapterInPart} - Page ${pageIndex + 1}`, `Partie ${part} - Chapitre ${chapterInPart} - Page ${pageIndex + 1}`);
        } else {
          // Format chapitre simple pour romans courts
          const chapter = Math.ceil((pageIndex + 1) / 5); // 5 pages par chapitre
          const totalChapters = Math.ceil(totalPages / 5);
          return tr(`Chapter ${chapter} of ${totalChapters} - Page ${pageIndex + 1}`, `Chapitre ${chapter} sur ${totalChapters} - Page ${pageIndex + 1}`);
        }
      }
    }
    
    // Default fallback
    return tr(`Page ${pageIndex + 1} of ${totalPages}`, `Page ${pageIndex + 1} sur ${totalPages}`);
  };

  const getStoryDuration = (storyParts) => {
    const totalWords = storyParts.reduce((total, part) => total + part.split(' ').length, 0);
    const avgReadingSpeed = 150; // mots par minute
    const minutes = Math.ceil(totalWords / avgReadingSpeed);
    return tr(`${minutes} min read`, `${minutes} min de lecture`);
  };

  // Age-appropriate styling function based on 2025 editorial standards for real books
  const getAgeGroupStyles = (age) => {
    // Determine age group from age string
    if (typeof age === 'string') {
      if (age.includes('3-4') || age.includes('3') || age.includes('4')) {
        return {
          textSize: 'text-lg md:text-xl',         // 18-22pt optimal pour 3-4 ans
          lineHeight: 'leading-relaxed',          // 1.5x optimal pour reconnaissance
          spacing: 'space-y-4 md:space-y-5',     // Espacement modéré
          fontWeight: 'font-bold',                // Maximum readability
          letterSpacing: 'tracking-normal',       // Lettres normales
          maxLines: '5-8',                        // 5-8 lignes max par page
          indent: false                           // Pas d'indentation
        };
      } else if (age.includes('5-7') || age.includes('5') || age.includes('6') || age.includes('7')) {
        return {
          textSize: 'text-lg md:text-xl',         // 16-20pt optimal pour early readers
          lineHeight: 'leading-relaxed',          // Aéré pour faciliter lecture
          spacing: 'space-y-4 md:space-y-5',     // Généreux mais moins qu'avant
          fontWeight: 'font-medium',              // Medium pour early readers
          letterSpacing: 'tracking-normal',
          maxLines: '4-5',                        // 4-5 sentences max par page
          indent: false                           // Paragraphes séparés par espaces
        };
      } else if (age.includes('8-10') || age.includes('8') || age.includes('9') || age.includes('10')) {
        return {
          textSize: 'text-sm md:text-base',        // 12-14pt pour chapter books
          lineHeight: 'leading-normal',            // Espacement standard
          spacing: 'space-y-2 md:space-y-3',      // Moins d'espacement entre paragraphes
          fontWeight: 'font-normal',               // Poids normal pour chapter books
          letterSpacing: 'tracking-normal',
          maxLines: '3-4',                         // 3-4 paragraphes par page
          indent: true                             // Indentation obligatoire
        };
      } else if (age.includes('11-13') || age.includes('11') || age.includes('12') || age.includes('13')) {
        return {
          textSize: 'text-xs md:text-sm',         // 11-13pt pour romans modernes
          lineHeight: 'leading-normal',           // Densité moderne
          spacing: 'space-y-1 md:space-y-2',     // Très compact comme vrais romans
          fontWeight: 'font-normal',
          letterSpacing: 'tracking-normal',
          maxLines: '4-5',                        // 4-5 paragraphes par page
          indent: true                            // Indentation moderne obligatoire
        };
      }
    }
    
    // Default for 5-7 years
    return {
      textSize: 'text-xl md:text-2xl',
      lineHeight: 'leading-relaxed',
      spacing: 'space-y-4 md:space-y-5',
      fontWeight: 'font-semibold',
      letterSpacing: 'tracking-normal',
      maxLines: '8-12',
      indent: false
    };
  };

  const MusicCard = ({ story }) => (
    <div 
      className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-[1.02] overflow-hidden"
      onClick={() => setSelectedStory(story)}
    >
      {/* Header avec titre */}
      <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-3 md:p-4">
        <h3 className="text-white font-bold text-base md:text-lg truncate flex items-center gap-2">
          <svg className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
          </svg>
          {story.title || `Chanson pour ${story.child_data?.name || 'mon enfant'}`}
        </h3>
        <div className="flex items-center justify-between mt-2 text-blue-100 text-xs md:text-sm">
          <div className="flex items-center gap-2">
            <img 
              src={userData?.profile?.profileImage || currentUser?.picture || '/default-avatar.png'} 
              alt="Family"
              className="w-4 h-4 rounded-full object-cover"
            />
            <span>{userData?.profile?.familyName || currentUser?.name || 'Family'}</span>
          </div>
          <div className="flex items-center gap-1">
            <Calendar className="w-3 h-3 md:w-4 md:h-4" />
            <span className="hidden sm:inline">{formatDate(story.createdAt || story.created_at)}</span>
            <span className="sm:hidden">{formatDate(story.createdAt || story.created_at).split(',')[0]}</span>
          </div>
        </div>
      </div>

      {/* Music Preview Area */}
      <div className="p-3 md:p-4">
        <div className="w-full h-32 md:h-48 bg-gradient-to-br from-blue-100 via-indigo-50 to-purple-100 rounded-xl shadow-lg flex items-center justify-center border-2 border-dashed border-blue-200">
          <div className="text-center text-blue-500">
            <div className="mb-2 relative">
              <div className="w-8 h-8 md:w-12 md:h-12 mx-auto bg-gradient-to-br from-blue-200 to-indigo-200 rounded-2xl flex items-center justify-center">
                <svg className="w-4 h-4 md:w-6 md:h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                </svg>
              </div>
              <div className="absolute -top-1 -right-1 w-4 h-4 md:w-5 md:h-5 bg-yellow-400 rounded-full flex items-center justify-center">
                <span className="text-xs">🎵</span>
              </div>
            </div>
            <p className="text-xs font-semibold text-blue-700">
              {tr('Personalized Song', 'Chanson personnalisée')}
            </p>
          </div>
        </div>
      </div>

      {/* Music Info */}
      <div className="px-3 pb-3 md:px-4 md:pb-4">
        <div className="bg-gradient-to-br from-blue-50 via-indigo-25 to-purple-50 rounded-xl p-3 md:p-4 border-l-4 border-blue-400 shadow-inner">
          <p className="text-gray-700 text-xs md:text-sm leading-relaxed">
            <strong>{tr('Child:', 'Enfant :')}</strong> {story.child_data?.name || tr('Unknown', 'Inconnu')}<br/>
            <strong>{tr('Age:', 'Âge :')}</strong> {story.child_data?.age || tr('Unknown', 'Inconnu')}<br/>
            <strong>{tr('Style:', 'Style :')}</strong> {story.child_data?.musical_style || tr('Custom', 'Personnalisé')}
          </p>
        </div>
      </div>
    </div>
  );

  const StoryCard = ({ story }) => (
    <div 
      className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-[1.02] overflow-hidden"
      onClick={() => setSelectedStory(story)}
    >
      {/* Header avec titre */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-3 md:p-4">
        <h3 className="text-white font-bold text-base md:text-lg truncate flex items-center gap-2">
          <Book className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0" />
          {story.title}
        </h3>
        <div className="flex items-center justify-between mt-2 text-indigo-100 text-xs md:text-sm">
          <div className="flex items-center gap-2">
            <img 
              src={userData?.profile?.profileImage || currentUser?.picture || '/default-avatar.png'} 
              alt="Family"
              className="w-4 h-4 rounded-full object-cover"
            />
            <span>{userData?.profile?.familyName || currentUser?.name || 'Family'}</span>
          </div>
          <div className="flex items-center gap-1">
            <Calendar className="w-3 h-3 md:w-4 md:h-4" />
            <span className="hidden sm:inline">{formatDate(story.createdAt || story.created_at)}</span>
            <span className="sm:hidden">{formatDate(story.createdAt || story.created_at).split(',')[0]}</span>
          </div>
        </div>
      </div>

      {/* Image en bandeau */}
      <div className="p-3 md:p-4">
        {story.imageUrl ? (
          <img 
            src={story.imageUrl} 
            alt={story.title}
            className="w-full rounded-xl shadow-lg"
            style={{ 
              height: 'auto',
              minHeight: '150px',
              maxHeight: '200px',
              objectFit: 'cover'
            }}
          />
        ) : (
          <div className="w-full h-32 md:h-48 bg-gradient-to-br from-purple-100 via-blue-50 to-indigo-100 rounded-xl shadow-lg flex items-center justify-center border-2 border-dashed border-indigo-200">
            <div className="text-center text-indigo-500">
              <div className="mb-2 relative">
                <div className="w-8 h-8 md:w-12 md:h-12 mx-auto bg-gradient-to-br from-purple-200 to-indigo-200 rounded-2xl flex items-center justify-center">
                  <Book className="w-4 h-4 md:w-6 md:h-6 text-indigo-600" />
                </div>
                <div className="absolute -top-1 -right-1 w-4 h-4 md:w-5 md:h-5 bg-yellow-400 rounded-full flex items-center justify-center">
                  <span className="text-xs">✨</span>
                </div>
              </div>
              <p className="text-xs font-semibold text-indigo-700">
                {tr('Story Illustration', "Illustration de l'histoire")}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Aperçu du contenu */}
      <div className="px-3 pb-3 md:px-4 md:pb-4">
        <div className="bg-gradient-to-br from-violet-50 via-purple-25 to-indigo-50 rounded-xl p-3 md:p-4 border-l-4 border-white shadow-inner">
          <p className="text-gray-700 text-xs md:text-sm leading-relaxed line-clamp-3">
            {story.storyParts[0]?.substring(0, 150)}...
          </p>
        </div>
      </div>
    </div>
  );

  const StoryReader = ({ story }) => {
    const totalParts = story.storyParts.length;
    const currentPart = story.storyParts[currentStoryPage] || "";

    const nextStoryPage = () => {
      if (currentStoryPage < totalParts - 1) {
        setCurrentStoryPage(currentStoryPage + 1);
      }
    };

    const prevStoryPage = () => {
      if (currentStoryPage > 0) {
        setCurrentStoryPage(currentStoryPage - 1);
      }
    };

    const storyPartTitles = [
      "Initial Situation",
      "Disrupting Element", 
      "Adventures",
      "Resolution"
    ];

    const getChapterInfo = (pageIndex, totalPages, age) => {
      // Determine age group for appropriate page indicators
      if (typeof age === 'string') {
        if (age.includes('3-4') || age.includes('3') || age.includes('4')) {
          return tr(`Image ${pageIndex + 1} of ${totalPages}`, `Image ${pageIndex + 1} sur ${totalPages}`);  // Simpler for youngest
        } else if ((age.includes('11-13') || age.includes('11') || age.includes('12') || age.includes('13')) && totalPages > 30) {
          // For mini-novels, show approximate chapters
          const chapter = Math.ceil((pageIndex + 1) / 10);
          return tr(`Chapter ${chapter} - Page ${pageIndex + 1} of ${totalPages}`, `Chapitre ${chapter} - Page ${pageIndex + 1} sur ${totalPages}`);
        }
      }
      
      // Default for most ages
      return tr(`Page ${pageIndex + 1} of ${totalPages}`, `Page ${pageIndex + 1} sur ${totalPages}`);
    };

    return (
      <div className="h-full bg-gradient-to-br from-indigo-100 via-purple-50 to-pink-100 overflow-y-auto">
        <div className="container mx-auto px-4 py-4 md:py-8 pb-32 md:pb-24 min-h-full">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            {/* Header avec bouton retour */}
            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-4 flex-shrink-0 rounded-t-3xl">
              <div className="flex items-center justify-between mb-2">
                <button
                  onClick={() => {
                    setSelectedStory(null);
                    setCurrentStoryPage(0);
                  }}
                  className="text-white hover:text-indigo-200 transition-colors flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4 md:w-5 md:h-5" />
                  <span className="text-xs md:text-sm">{tr('Back to My Stories', 'Retour à mes histoires')}</span>
                </button>
                <div className="text-indigo-200 text-xs md:text-sm">
                </div>
              </div>
              <div className="text-center">
                <h1 className="text-xl md:text-2xl font-bold text-white mb-1 flex items-center justify-center gap-2">
                  <Book className="w-5 h-5 md:w-6 md:h-6" />
                  {story.title}
                </h1>
                <div className="flex items-center justify-between mt-2 text-indigo-100 text-sm">
                  <div className="flex items-center gap-2">
                    <img 
                      src={userData?.profile?.profileImage || currentUser?.picture || '/default-avatar.png'} 
                      alt="Family"
                      className="w-5 h-5 rounded-full object-cover"
                    />
                    <span>{userData?.profile?.familyName || currentUser?.name || 'Family'}</span>
                  </div>
                  <span>{formatDate(story.createdAt || story.created_at)}</span>
                </div>
              </div>
            </div>
            
            {/* Story Parameters Banner */}
            <StoryParametersBanner 
              parameters={{
                name: story.originalData?.request?.name || story.storyRequest?.name,
                age: story.originalData?.request?.age || story.storyRequest?.age,
                gender: story.originalData?.request?.gender || story.storyRequest?.gender,
                interests: story.originalData?.request?.interests || story.storyRequest?.interests,
                style: story.originalData?.request?.style || story.storyRequest?.style,
                lesson: story.originalData?.refined_lesson || story.refined_lesson || story.originalData?.request?.lesson || story.storyRequest?.lesson
              }}
              onParameterUpdate={null} // Read-only in MyStoriesPage
              editable={false}
              headerText={tr('Story Criterias', "Critères de l'histoire")}
              disableAnimations={true}
            />
            
            {/* Fixed Image Banner */}
            <div className="p-4 flex-shrink-0">
              {story.imageUrl ? (
                <img 
                  src={story.imageUrl} 
                  alt={`${story.title} - Story Illustration`}
                  className="w-full rounded-xl shadow-lg block"
                  style={{ 
                    height: 'auto',
                    minHeight: '150px',
                    maxHeight: '300px',
                    objectFit: 'cover'
                  }}
                />
              ) : (
                <div className="w-full h-32 md:h-48 bg-gradient-to-br from-purple-100 via-blue-50 to-indigo-100 rounded-xl shadow-lg flex items-center justify-center border-2 border-dashed border-indigo-200">
                  <div className="text-center text-indigo-500">
                    <div className="mb-2 relative">
                      <div className="w-8 h-8 md:w-12 md:h-12 mx-auto bg-gradient-to-br from-purple-200 to-indigo-200 rounded-2xl flex items-center justify-center">
                        <Book className="w-4 h-4 md:w-6 md:h-6 text-indigo-600" />
                      </div>
                      <div className="absolute -top-1 -right-1 w-4 h-4 md:w-5 md:h-5 bg-yellow-400 rounded-full flex items-center justify-center">
                        <span className="text-xs">✨</span>
                      </div>
                    </div>
                    <p className="text-xs font-semibold text-indigo-700">
                      {tr('Story Illustration', "Illustration de l'histoire")}
                    </p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Story Content - HAUTEUR FIXE FORMAT LIVRE */}
            <div className="p-4">
              <div 
                className="bg-gradient-to-br from-violet-50 via-purple-25 to-indigo-50 rounded-2xl p-8 border-l-6 border-white shadow-inner"
                style={{
                  height: window.innerWidth < 768 ? '400px' : '600px',
                  overflowY: window.innerWidth < 768 ? 'auto' : 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  backgroundColor: (story.originalData?.request?.age || story.storyRequest?.age || '5-7').includes('3-4') || 
                                 (story.originalData?.request?.age || story.storyRequest?.age || '5-7').includes('3') || 
                                 (story.originalData?.request?.age || story.storyRequest?.age || '5-7').includes('4') ? '#fef7ff' : undefined,
                  border: (story.originalData?.request?.age || story.storyRequest?.age || '5-7').includes('3-4') || 
                         (story.originalData?.request?.age || story.storyRequest?.age || '5-7').includes('3') || 
                         (story.originalData?.request?.age || story.storyRequest?.age || '5-7').includes('4') ? '2px solid #e5e7eb' : undefined
                }}
              >
                <div className="font-serif flex-1">
                  {/* Format livre par âge avec formatage intelligent */}
                  {formatStoryText(currentPart, story.originalData?.request?.age || story.storyRequest?.age || '5-7') || (
                    <div className={`text-gray-800 ${getAgeGroupStyles(story.originalData?.request?.age || story.originalData?.storyRequest?.age || '5-7').spacing}`}>
                      {currentPart.split(/\n\n|\.\s+/).filter(sentence => sentence.trim()).map((sentence, index) => (
                        <p key={index} className="text-gray-800 text-justify">
                          {sentence.trim()}{!sentence.endsWith('.') && !sentence.endsWith('!') && !sentence.endsWith('?') ? '.' : ''}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Navigation Footer - Fixed at bottom */}
            <div className="bg-white border-t border-gray-200 p-4 md:p-6 flex-shrink-0 rounded-b-3xl">
              <div className="flex justify-between items-center mb-4 md:mb-6">
                <button
                  onClick={prevStoryPage}
                  disabled={currentStoryPage === 0}
                  className="flex items-center gap-1 md:gap-2 text-purple-500 hover:text-purple-700 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors duration-200 font-medium text-sm md:text-base"
                >
                  <ArrowLeft className="w-4 h-4 md:w-5 md:h-5" />
                  {tr('Previous', 'Précédent')}
                </button>
                
                <div className="text-center">
                  <span className="text-gray-500 text-xs md:text-sm">
                    Page {currentStoryPage + 1} {tr('of', 'sur')} {totalParts}
                  </span>
                </div>
                
                <button
                  onClick={nextStoryPage}
                  disabled={currentStoryPage === totalParts - 1}
                  className="flex items-center gap-1 md:gap-2 text-purple-500 hover:text-purple-700 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors duration-200 font-medium text-sm md:text-base"
                >
                  {tr('Next', 'Suivant')}
                  <ArrowLeft className="w-4 h-4 md:w-5 md:h-5 transform rotate-180" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Si une histoire est sélectionnée, afficher le reader
  if (selectedStory) {
    return <StoryReader story={selectedStory} />;
  }

  return (
    <div className="h-full bg-gradient-to-br from-indigo-100 via-purple-50 to-pink-100">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-4 md:py-6">
          <div className="text-center mb-4">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 flex items-center justify-center gap-3">
              <Book className="w-6 h-6 md:w-8 md:h-8 text-indigo-500" />
              {tr('My Stories', 'Mes Histoires')}
            </h1>
            <p className="text-gray-600 mt-1 text-sm md:text-base">{tr('Your personal collection of magical content', 'Votre collection personnelle de contenu magique')}</p>
          </div>
          
          {/* Modern Tabs */}
          <div className="flex justify-center">
            <div className="bg-gray-100 rounded-2xl p-2 flex gap-2">
              <button
                onClick={() => setActiveSection('whizz')}
                className={`px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-300 flex items-center gap-2 ${
                  activeSection === 'whizz'
                    ? 'bg-white text-purple-600 shadow-lg transform scale-105'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <div className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500"></div>
                WhizZ Collection
                {whizzCount > 0 && (
                  <span className="bg-purple-100 text-purple-600 px-2 py-1 rounded-full text-xs font-bold">
                    {whizzCount}
                  </span>
                )}
              </button>
              
              {/* Z Collection tab hidden while Z features are disabled */}
              {false && (
              <button
                onClick={() => setActiveSection('z')}
                className={`px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-300 flex items-center gap-2 ${
                  activeSection === 'z'
                    ? 'bg-white text-indigo-600 shadow-lg transform scale-105'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Z Collection
                {zCount > 0 && (
                  <span className="bg-indigo-100 text-indigo-600 px-2 py-1 rounded-full text-xs font-bold">
                    {zCount}
                  </span>
                )}
              </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-6 md:py-8 pb-32 md:pb-24">
        {loading ? (
          <LoadingScreen />
        ) : (
          <>
            {/* Section Content */}
            {activeSection === 'whizz' ? (
              <>
                {/* WhizZ Section */}
                <div className="mb-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-4 h-4 rounded-full bg-gradient-to-r from-purple-500 to-pink-500"></div>
                    <h2 className="text-xl font-bold text-gray-800">{tr('Moment of Serenity & Bedtime Stories', 'Moment de sérénité & histoires du soir')}</h2>
                  </div>
                  <p className="text-gray-600 text-sm mb-6">
                    {tr('Peaceful bedtime stories and calming content generated with WhizZ', 'Histoires du soir paisibles et contenu apaisant générés avec WhizZ')}
                  </p>
                </div>

                {/* Lullaby Carousel */}
                <LullabyCarousel />
                
                {filteredStories.length === 0 ? (
                  <div className="text-center py-20">
                    <div className="mb-6">
                      <div className="w-24 h-24 mx-auto bg-gradient-to-r from-purple-100 to-pink-100 rounded-3xl flex items-center justify-center">
                        <Book className="w-12 h-12 text-purple-400" />
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800 mb-4">{tr('No WhizZ Stories Yet', "Pas encore d'histoires WhizZ")}</h3>
                    <p className="text-gray-600 mb-6 max-w-md mx-auto">
                      {tr('Start creating magical bedtime stories! Every story you generate will be saved here for peaceful moments.', 'Commencez à créer des histoires du soir magiques ! Chaque histoire générée sera sauvegardée ici pour vos moments paisibles.')}
                    </p>
                    <button
                      onClick={() => setActiveTab && setActiveTab('WhizZ')}
                      className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105 shadow-lg"
                    >
                      {tr('Create Your First Story', 'Créez votre première histoire')}
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                      {filteredStories.map((story) => (
                        <StoryCard key={story.id} story={story} />
                      ))}
                    </div>
                    
                    {/* Pagination for WhizZ Section */}
                    {totalPages > 1 && (
                      <div className="flex justify-center items-center mt-8 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={goToPrevPage}
                            disabled={currentPage === 1 || loadingPage}
                            className="flex items-center gap-1 px-4 py-2 bg-white border border-purple-200 rounded-lg text-purple-600 hover:bg-purple-50 disabled:text-gray-400 disabled:bg-gray-50 disabled:cursor-not-allowed transition-all duration-200"
                          >
                            <ArrowLeft className="w-4 h-4" />
                            {tr('Previous', 'Précédent')}
                          </button>
                          
                          <div className="flex items-center gap-1 mx-4">
                            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                              const pageNum = i + 1;
                              return (
                                <button
                                  key={pageNum}
                                  onClick={() => goToPage(pageNum)}
                                  disabled={loadingPage}
                                  className={`w-8 h-8 rounded-lg text-sm font-medium transition-all duration-200 ${
                                    currentPage === pageNum
                                      ? 'bg-purple-500 text-white'
                                      : 'bg-white border border-purple-200 text-purple-600 hover:bg-purple-50'
                                  } disabled:cursor-not-allowed`}
                                >
                                  {loadingPage && currentPage === pageNum ? '...' : pageNum}
                                </button>
                              );
                            })}
                            {totalPages > 5 && (
                              <span className="text-gray-400 px-2">...</span>
                            )}
                          </div>
                          
                          <button
                            onClick={goToNextPage}
                            disabled={currentPage === totalPages || loadingPage}
                            className="flex items-center gap-1 px-4 py-2 bg-white border border-purple-200 rounded-lg text-purple-600 hover:bg-purple-50 disabled:text-gray-400 disabled:bg-gray-50 disabled:cursor-not-allowed transition-all duration-200"
                          >
                            {tr('Next', 'Suivant')}
                            <ArrowLeft className="w-4 h-4 transform rotate-180" />
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              <>
                {/* Z Section */}
                <div className="mb-8">
                  <div className="flex items-center gap-3 mb-6">
                    <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    <h2 className="text-xl font-bold text-gray-800">Interactive & Creative Content</h2>
                  </div>
                  <p className="text-gray-600 text-sm mb-6">
                    Music, educational games, and interactive stories generated with Z
                  </p>
                </div>

                {/* Z Content Categories */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  {/* Music */}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl p-6 border border-blue-200">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                        </svg>
                      </div>
                      <h3 className="font-bold text-gray-800">Music</h3>
                    </div>
                    <p className="text-gray-600 text-sm">Custom melodies and songs</p>
                  </div>

                  {/* Games */}
                  <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-2xl p-6 border border-green-200">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                        </svg>
                      </div>
                      <h3 className="font-bold text-gray-800">Educational Games</h3>
                    </div>
                    <p className="text-gray-600 text-sm">Interactive learning experiences</p>
                  </div>

                  {/* Interactive Stories */}
                  <div className="bg-gradient-to-br from-purple-50 to-violet-100 rounded-2xl p-6 border border-purple-200">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-purple-500 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <h3 className="font-bold text-gray-800">Interactive Stories</h3>
                    </div>
                    <p className="text-gray-600 text-sm">Choose-your-own adventures</p>
                  </div>
                </div>

                {/* Music Carousel for Z Collection */}
                <MusicCarousel userMusic={filteredStories.filter(story => story.type === 'music')} />

                {filteredStories.length === 0 ? (
                  <div className="text-center py-20">
                    <div className="mb-6">
                      <div className="w-24 h-24 mx-auto bg-gradient-to-r from-indigo-100 to-blue-100 rounded-3xl flex items-center justify-center">
                        <svg className="w-12 h-12 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800 mb-4">No Z Content Yet</h3>
                    <p className="text-gray-600 mb-6 max-w-md mx-auto">
                      Start creating interactive content! Music, games, and adventures will appear here once you generate them with Z.
                    </p>
                    <button
                      onClick={() => setActiveTab && setActiveTab('Z')}
                      className="bg-gradient-to-r from-indigo-500 to-blue-500 hover:from-indigo-600 hover:to-blue-600 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105 shadow-lg"
                    >
                      Explore Z Features
                    </button>
                  </div>
                ) : (
                  <>

                    {/* Z Content Grid by Type */}
                    <div className="space-y-8">
                      {/* Music Section */}
                      {filteredStories.filter(story => story.type === 'music').length > 0 && (
                        <div>
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                              </svg>
                            </div>
                            <h3 className="text-lg font-bold text-gray-800">Generated Music</h3>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-8">
                            {filteredStories.filter(story => story.type === 'music').map((story) => (
                              <StoryCard key={story.id} story={story} />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Games Section */}
                      {filteredStories.filter(story => story.type === 'game').length > 0 && (
                        <div>
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                              </svg>
                            </div>
                            <h3 className="text-lg font-bold text-gray-800">Educational Games</h3>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-8">
                            {filteredStories.filter(story => story.type === 'game').map((story) => (
                              <StoryCard key={story.id} story={story} />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Other Z Stories Section */}
                      {filteredStories.filter(story => story.type === 'z-story').length > 0 && (
                        <div>
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                            <h3 className="text-lg font-bold text-gray-800">Interactive Stories</h3>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                            {filteredStories.filter(story => story.type === 'z-story').map((story) => (
                              <StoryCard key={story.id} story={story} />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Pagination for Z Section */}
                    {totalPages > 1 && (
                      <div className="flex justify-center items-center mt-8 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={goToPrevPage}
                            disabled={currentPage === 1 || loadingPage}
                            className="flex items-center gap-1 px-4 py-2 bg-white border border-indigo-200 rounded-lg text-indigo-600 hover:bg-indigo-50 disabled:text-gray-400 disabled:bg-gray-50 disabled:cursor-not-allowed transition-all duration-200"
                          >
                            <ArrowLeft className="w-4 h-4" />
                            Previous
                          </button>
                          
                          <div className="flex items-center gap-1 mx-4">
                            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                              const pageNum = i + 1;
                              return (
                                <button
                                  key={pageNum}
                                  onClick={() => goToPage(pageNum)}
                                  disabled={loadingPage}
                                  className={`w-8 h-8 rounded-lg text-sm font-medium transition-all duration-200 ${
                                    currentPage === pageNum
                                      ? 'bg-indigo-500 text-white'
                                      : 'bg-white border border-indigo-200 text-indigo-600 hover:bg-indigo-50'
                                  } disabled:cursor-not-allowed`}
                                >
                                  {loadingPage && currentPage === pageNum ? '...' : pageNum}
                                </button>
                              );
                            })}
                            {totalPages > 5 && (
                              <span className="text-gray-400 px-2">...</span>
                            )}
                          </div>
                          
                          <button
                            onClick={goToNextPage}
                            disabled={currentPage === totalPages || loadingPage}
                            className="flex items-center gap-1 px-4 py-2 bg-white border border-indigo-200 rounded-lg text-indigo-600 hover:bg-indigo-50 disabled:text-gray-400 disabled:bg-gray-50 disabled:cursor-not-allowed transition-all duration-200"
                          >
                            Next
                            <ArrowLeft className="w-4 h-4 transform rotate-180" />
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MyStoriesPage;